import { ApiEndPoints, API_QUERY_PARAMS, FileType } from '../../../polly-manage.constants';
import {
  CreateWorkspacePayload, NotebookDetailsInterface, UpdateWorkspacePayload,
  WorkspacePermissionsPayload, ESWorkspaceTagsAttributes, ESWorkspaceAttributes,
  ComputeApplicationsResponse
} from '../../interfaces';
import { forkJoin, Observable } from 'rxjs';
import {
  IndexedDB, JupyterEnvsResponse, MithooApiV2Service, NoteBookResponse, PollyNotebooksApiService, UpdateNotebookPayload,
  CommonApiService, WorkspaceFileUploadInterface, WorkspaceResponse, MachinesListResponse, ElasticApiResponse,
  UserResponse, UserLicensePayload, UserListResponse, ComponentListResponse,
  OrganizationsListResponse
} from 'polly-commons';
import { Injectable } from '@angular/core';
import { ES_WORKSPACES_ENDPOINT } from '../../../modules/workspace-module/polly-manage-workspace.constants';
import { buildTagSuggestionQuery, buildWorkspaceDetailsQuery } from '../../utilities/helper-functions/workspaces.utilities';
import { map } from 'rxjs/operators';
import { NgxIndexedDBService } from 'ngx-indexed-db';

@Injectable({
  providedIn: 'root',
})
export class PollyManageBackendService {
  public indexedDB: IndexedDB;
  constructor(
    private readonly idxDBService: NgxIndexedDBService,
    private readonly mithooV2ApiService: MithooApiV2Service,
    private readonly commonApiService: CommonApiService,
    private readonly notebooksApiService: PollyNotebooksApiService
  ) {
    this.indexedDB = new IndexedDB(this.idxDBService, 'manage_store');
  }

  public fetchWorkspacesFromES(url: string, payload: object): Observable<ElasticApiResponse<ESWorkspaceAttributes, undefined>> {
    return this.commonApiService.post(url, payload);
  }

  public searchOrganizations(query: string | undefined, size: number = 30): Observable<OrganizationsListResponse> {
    let getAllOrganizationsEndPoint: string = `/auth/organizations?page[size]=${size}&filter[active]=true`;
    if (query !== undefined) {
      getAllOrganizationsEndPoint += `&filter[search]=${query}`;
    }
    return this.commonApiService.get(getAllOrganizationsEndPoint);
  }

  public searchUsers(searchQuery?: string): Observable<UserListResponse> {
    let queryParams: string = `${ApiEndPoints.AUTH_USERS}?filter[active]=true`;
    if (!!searchQuery) {
      queryParams += `&filter[search]=${searchQuery}&page[size]=25`;
    }
    return this.commonApiService.get(queryParams);
  }

  public searchNextUsers(nextUrl: string): Observable<UserListResponse> {
    return this.commonApiService.get(nextUrl);
  }

  /**
   * This function fetches all the components
   */
  public getAllComponents(): Observable<ComponentListResponse> {
    return this.commonApiService.get(ApiEndPoints.GET_COMPONENTS);
  }

  /**
   * Fetches workspace information along with the information regarding it's creator.
   * @param id of type number
   */
  public getSelectedWorkspace(id: number): Observable<ElasticApiResponse<ESWorkspaceAttributes, undefined>> {
    const payload: object = buildWorkspaceDetailsQuery(id);
    return this.commonApiService.post(ES_WORKSPACES_ENDPOINT, payload).pipe(
      map((response: ElasticApiResponse<ESWorkspaceAttributes, undefined>) => {
        if (response.hits.hits.length === 0) throw new Error();
        return response;
      })
    );
  }

  /**
   * This function makes a post request to create a new workspace
   */
  public createWorkspace(payloadWorkspace: CreateWorkspacePayload): Observable<WorkspaceResponse> {
    return this.commonApiService.post(this.getMithooUrl('workspaces'), JSON.stringify(payloadWorkspace));
  }

  /**
   * This function makes a post request to create a new workspace
   */
  public editWorkspace(url: string, payloadWorkspace: UpdateWorkspacePayload): Observable<void> {
    return this.commonApiService.patch(this.getMithooUrl(url), payloadWorkspace);
  }

  /**
   * Get available tags for the organization, Used for tagging the workspace
   *
   * @param tagSearchQuery search query for tags
   * @return Observable<ElasticApiResponse<ESWorkspaceTagsAttributes, undefined>>
   */
  public getTagsForOrganization(tagSearchQuery: string): Observable<ElasticApiResponse<ESWorkspaceTagsAttributes, undefined>> {
    const url: string = `/tags/_search`;
    const payload = buildTagSuggestionQuery(tagSearchQuery);
    return this.commonApiService.post(this.getMithooUrl(url), payload);
  }

  public toggleFavouriteWorkspace(workspaceId: string, favourite: boolean): Observable<void> {
    const updateWorkspacePayload: UpdateWorkspacePayload = {
      data: {
        type: 'workspaces',
        id: workspaceId,
        attributes: { favourite },
      },
    };
    return this.editWorkspace(`workspaces/${workspaceId}`, updateWorkspacePayload);
  }

  public toggleWatchWorkspace(workspaceId: string, watch: boolean): Observable<void> {
    const updateWorkspacePayload: UpdateWorkspacePayload = {
      data: {
        type: 'workspaces',
        id: workspaceId,
        attributes: { watch },
      },
    };
    return this.editWorkspace(`workspaces/${workspaceId}`, updateWorkspacePayload);
  }

  /* This function is used to send an delete request
   * @param url of type string
   */
  public deleteRequest<T>(url: string): Observable<T> {
    return this.mithooV2ApiService.delete(url);
  }

  /**
   * This function sends delete request in bulk.
   * @param deleteUrls of type strings[]
   */
  public deleteMultipleFiles(deleteUrls: { url: string; fileType: string }[]): Observable<void[]> {
    const deleteFileUrls: Observable<void>[] = [];
    deleteUrls.forEach((deleteUrl) => {
      if (FileType.NOTEBOOK === deleteUrl.fileType) {
        deleteFileUrls.push(this.deleteRequest(deleteUrl.url));
      } else if (deleteUrl.fileType === FileType.RUNS) {
        deleteFileUrls.push(this.commonApiService.delete(deleteUrl.url));
      } else {
        deleteFileUrls.push(this.commonApiService.delete(this.getMithooUrl(deleteUrl.url)));
      }
    });
    return forkJoin(deleteFileUrls);
  }

  /**
   * This function will add workspace permissions to the workspace id
   * @param workspaceId id of the workspace
   * @param wsPermissionsPayload payload for workspace creation
   */
  public addWorkspacePermissions(workspaceId: number, wsPermissionsPayload: WorkspacePermissionsPayload): Observable<any> {
    const url: string = `${ApiEndPoints.GET_WORKSPACES_ENDPOINT}/${workspaceId}/${ApiEndPoints.GET_WORKSPACE_PERMISSIONS}`;
    return this.commonApiService.post(this.getMithooUrl(url), wsPermissionsPayload);
  }

  /**
   * This function sends get request
   */
  public getReq<T>(url: string): Observable<T> {
    return this.mithooV2ApiService.get(url);
  }

  /**
   * HTTP Patch request
   * @param url URL
   * @param payload payload for patch
   */
  public patchRequest<T>(url: string, payload?: any): Observable<T> {
    return this.mithooV2ApiService.patch(url, payload);
  }

  /**
   * This function is used to send post req
   * @param url of type string
   * @param payload of type any
   */
  public postReq<T>(url: string, payload: any): Observable<T> {
    return this.mithooV2ApiService.post(url, payload);
  }

  /**
   * This function is used to send base Api post req
   * @param url endpoint
   * @param payload request payload
   */
  public commonApiPostReq<T>(url: string, payload: any): Observable<T> {
    return this.commonApiService.post(this.getMithooUrl(url), payload);
  }

  /**
   * This function is used to send base Api get req
   * @param url endpoint
   */
  public commonApiGetReq<T>(url: string): Observable<T> {
    return this.commonApiService.get(this.getMithooUrl(url));
  }

  /**
   * This function is used to send base Api patch req
   * @param url endpoint
   * @param payload request payload
   */
  public commonApiPatchReq<T>(url: string, payload?: any): Observable<T> {
    return this.commonApiService.patch(this.getMithooUrl(url), payload);
  }

  /**
   * This function is called to update term and condition
   * acceptance status details.
   *  @param userId of type number or string
   */
  public onAcceptingTerms(userId: string | number): Observable<UserResponse> {
    const userPayload: UserLicensePayload = {
      data: {
        attributes: {
          license_info: {
            license_accepted: true,
          },
        },
        type: 'eula',
        id: userId.toString(),
      },
    };
    return this.commonApiService.post('auth/eula', userPayload);
  }
  /**
   * This function will fetch the information of the current user
   * including its license
   */
  public getCurrentUserInfo(): Observable<UserResponse> {
    const url: string = `auth/${ApiEndPoints.USER}/me?include=organizations,licenses,roles`;
    return this.commonApiService.get(url);
  }

  public getJupyterEnvs(): Observable<JupyterEnvsResponse> {
    const url: string = `/compute/resources/environments?filter[scope]=global&filter[resource_access]=public`;
    return this.commonApiService.get(url);
  }
  /**
   * Get Notebook details along with SageMakerSpec and Versions
   */
  public getSagemakerNotebookDetails(notebookSelfUrl: string): Observable<NotebookDetailsInterface> {
    const toBeInclude: string = encodeURIComponent(`${ApiEndPoints.NOTEBOOK_DETAILS_PARAM_VALUE_V2},${ApiEndPoints.VERSIONS}`);
    const versionsPageSize: string = encodeURIComponent(
      `${API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_QUERY_PARAM}=${ApiEndPoints.VERSIONS_PAGE_SIZE_VALUE}`
    );
    const queryString: string = `${ApiEndPoints.INCLUDE}${toBeInclude}&${versionsPageSize}`;
    const url: string = `${notebookSelfUrl}&${queryString}`;
    return this.mithooV2ApiService.get(url);
  }

  /**
   * @todo Move this to an appropriate place
   *
   * @param workspaceId in which notebook is being cloned
   * @param notebookName Name of the notebook
   * @param clonedNotebookName Name of the new cloned notebook
   */
  public cloneNotebook(workspaceId: string, notebookName: string, clonedNotebookName: string): Observable<NoteBookResponse> {
    const payload: UpdateNotebookPayload = {
      data: {
        type: 'notebooks',
        attributes: {
          notebook_name: clonedNotebookName,
        },
      },
    };
    return this.notebooksApiService.clone(+workspaceId, notebookName, payload);
  }

  /**
   * This function is making a api call to get the preSigned url for upload files from the uppy.
   */
  public getPresignedUrl(filePath: string, workspaceId: string, fileContentType: string): Observable<WorkspaceFileUploadInterface> {
    // For removing the workspaceID and '/' from the filePath and also encode the filepath.
    filePath = encodeURIComponent(filePath.slice(workspaceId.length + 1));
    const url: string = `/workspaces/${workspaceId}/upload_url?file_path=${filePath}&content_type=${fileContentType}`;
    return this.commonApiGetReq(url);
  }

  /**
   * This function calls all the machine data for notebooks
   */
  public fetchNotebookMachines(): Observable<MachinesListResponse> {
    return this.commonApiService.get('/compute/machine-components?include[specs]=true&filter[service]=notebooks');
  }

  public getMithooUrl(url: string): string {
    return 'mithoo/' + (url.startsWith('/') ? url.slice(1) : url);
  }

  /**
   * This function fetches all the components
   */
  public getUserSpecificAllComponents(): Observable<ComputeApplicationsResponse> {
    return this.commonApiService.get(ApiEndPoints.GET_USER_APPLICATION);
  }
}
